import { Stack, Typography } from '@mui/material'

import {
  CommentsConnectionFragment,
  Maybe,
  TradeBoxFragment,
  TradeNegotiation,
  TradeNegotiationStatus,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  TradeDetailBox,
  TradeDetailBoxSkeleton,
  TradeMessageBox,
  TradeMessageBoxSkeleton,
} from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { config } from '@/config'
import { User as AuthUser, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  currentUser: Maybe<AuthUser>
  trade: Maybe<Pick<TradeNegotiation, 'formattedExpiredAt'> & TradeBoxFragment>
  messages: Maybe<CommentsConnectionFragment>
}

export type Action = {
  onFetchMore: () => Promise<void>
}

export const UserTradeDetailTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('userTradeDetail')
  const { routerNames, generatePath, routerPages } = useRouter()

  const artistId =
    state.trade?.candidates[0].candidateItems[0].item.issuedArtist?.id

  return (
    <BaseLayout
      header={{
        title: state.trade?.status ? t(state.trade?.status) : '',
        back: true,
        type: 'modal',
        defaultBackPage: artistId
          ? config.applicationType === 'standalone'
            ? generatePath(routerPages.ArtistTop, {
                artistId,
              })
            : generatePath(routerPages.ArtistTrade, {
                artistId,
              })
          : undefined,
      }}
    >
      <Head title={routerNames.UserTradeDetail} />
      {state.trade?.status === TradeNegotiationStatus.InProgress && (
        <TextCell trade={state.trade} />
      )}
      {!state.loading && state.trade ? (
        <TradeDetailBox
          trade={state.trade}
          myUserId={state.currentUser?.uid ?? ''}
        />
      ) : (
        <TradeDetailBoxSkeleton />
      )}
      <ScrollFetcher
        componentProps={{ spacing: 0 }}
        nodes={state.messages?.edges.map(({ node }, index) => (
          <TradeMessageBox message={node} key={index} />
        ))}
        loading={state.loading}
        hasNextPage={state.hasNextPage}
        onFetchMore={onFetchMore}
        loadingComponent={
          <Stack sx={{ px: 5 }}>
            {[1, 2, 3].map(key => (
              <TradeMessageBoxSkeleton key={key} />
            ))}
          </Stack>
        }
      />
    </BaseLayout>
  )
}

type TextCellProps = {
  trade: State['trade']
}
export const TextCell: Cell<TextCellProps> = ({ trade }) => {
  const { t } = useTranslation('userTradeDetail')
  return (
    <CellFrame sx={{ py: 2, bgcolor: 'grey.50' }}>
      <Stack spacing={1} alignItems="center">
        <Typography variant="caption">{t('tradeAlert')}</Typography>
        <Typography variant="overline" color="text.secondary">
          {t('appDeadline', { expiredAt: trade?.formattedExpiredAt })}
        </Typography>
      </Stack>
    </CellFrame>
  )
}
